import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bug, Activity, CheckCircle, XCircle, Zap, Play, Square, 
  History, Bot, Timer, Loader2, Check, AlertTriangle, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminSystemTab - System monitoring, E2E testing, and AI testing controls
 */
export default function AdminSystemTab({
  systemHealth,
  errorStats,
  systemErrors,
  aiTestingStatus,
  aiTestHistory,
  aiAutoFixes,
  latestTestRun,
  e2eTestReports,
  selectedE2eReport,
  setSelectedE2eReport,
  fetchAllData,
  fetchE2eReports
}) {
  const [e2eTestRunning, setE2eTestRunning] = useState(false);
  const [aiTestingLoading, setAiTestingLoading] = useState(false);

  // E2E Test Functions
  const runE2eTests = async () => {
    setE2eTestRunning(true);
    try {
      toast.info("Running E2E test suite... This may take a minute.");
      const response = await axios.post(`${API}/admin/e2e-tests/run`);
      const result = response.data;
      
      if (result.summary.tests_failed > 0) {
        toast.warning(`E2E Tests: ${result.summary.tests_passed}/${result.summary.tests_run} passed. ${result.summary.issues_found} issues found.`);
      } else {
        toast.success(`E2E Tests: All ${result.summary.tests_run} tests passed!`);
      }
      
      setSelectedE2eReport(result);
      fetchE2eReports();
    } catch (error) {
      toast.error("E2E test suite failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setE2eTestRunning(false);
    }
  };

  const cleanupOrphanedTestData = async () => {
    try {
      const response = await axios.delete(`${API}/admin/e2e-tests/cleanup`);
      toast.success(`Cleaned up: ${JSON.stringify(response.data.deleted)}`);
    } catch (error) {
      toast.error("Cleanup failed: " + (error.response?.data?.detail || error.message));
    }
  };

  // AI Testing handlers
  const handleStartAiTesting = async () => {
    setAiTestingLoading(true);
    try {
      await axios.post(`${API}/ai-testing/start?interval_seconds=3600`);
      toast.success("AI Testing started - runs every hour");
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to start AI testing");
    } finally {
      setAiTestingLoading(false);
    }
  };

  const handleStopAiTesting = async () => {
    setAiTestingLoading(true);
    try {
      await axios.post(`${API}/ai-testing/stop`);
      toast.success("AI Testing stopped");
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Failed to stop AI testing");
    } finally {
      setAiTestingLoading(false);
    }
  };

  const handleRunTestsNow = async () => {
    setAiTestingLoading(true);
    toast.info("Running tests...");
    try {
      const res = await axios.post(`${API}/ai-testing/run-now`);
      const result = res.data;
      if (result.health_status === 'healthy') {
        toast.success(`All tests passed! ${result.total_passed}/${result.total_tests}`);
      } else if (result.health_status === 'degraded') {
        toast.warning(`Tests completed with issues: ${result.total_passed}/${result.total_tests} passed`);
      } else {
        toast.error(`Tests failed: ${result.total_passed}/${result.total_tests} passed`);
      }
      fetchAllData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === "string" ? error.response?.data?.detail : JSON.stringify(error.response?.data?.detail) || "Test run failed");
    } finally {
      setAiTestingLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Health Status Banner */}
      <div className={`rounded-xl p-4 flex items-center justify-between ${
        systemHealth === null 
          ? 'bg-gray-50 border border-gray-200' 
          : systemHealth?.healthy 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center gap-3">
          {systemHealth === null ? (
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          ) : systemHealth?.healthy ? (
            <CheckCircle className="w-8 h-8 text-green-600" />
          ) : (
            <XCircle className="w-8 h-8 text-red-600" />
          )}
          <div>
            <h3 className={`font-semibold ${
              systemHealth === null 
                ? 'text-gray-700' 
                : systemHealth?.healthy 
                  ? 'text-green-900' 
                  : 'text-red-900'
            }`}>
              {systemHealth === null 
                ? 'Checking System Health...' 
                : `System ${systemHealth?.healthy ? 'Healthy' : 'Issues Detected'}`}
            </h3>
            <p className="text-sm text-gray-600">
              Last check: {systemHealth?.last_check ? new Date(systemHealth.last_check).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              await axios.post(`${API}/system/trigger-recovery?component=all`);
              toast.success("Recovery triggered");
              fetchAllData();
            } catch (e) {
              toast.error("Recovery failed");
            }
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
        >
          <Zap className="w-4 h-4" /> Trigger Recovery
        </button>
      </div>

      {/* Error Stats */}
      {errorStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-gray-900">{errorStats.total_errors || 0}</p>
            <p className="text-xs text-gray-500">Total Errors</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-red-600">{errorStats.unresolved || 0}</p>
            <p className="text-xs text-gray-500">Unresolved</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-amber-600">{errorStats.last_hour || 0}</p>
            <p className="text-xs text-gray-500">Last Hour</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-blue-600">{errorStats.last_24h || 0}</p>
            <p className="text-xs text-gray-500">Last 24h</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-green-600">{errorStats.auto_fixed || 0}</p>
            <p className="text-xs text-gray-500">Auto-Fixed</p>
          </div>
        </div>
      )}

      {/* E2E Test Suite Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-brand" /> Comprehensive E2E Test Suite
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Automatically tests all features: venues, owners, contributors, reviews. Creates test data, validates, and cleans up.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={cleanupOrphanedTestData}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Cleanup Test Data
            </button>
            <button
              onClick={runE2eTests}
              disabled={e2eTestRunning}
              className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-dark flex items-center gap-2 disabled:opacity-50"
              data-testid="run-e2e-tests-btn"
            >
              {e2eTestRunning ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
              ) : (
                <><Zap className="w-4 h-4" /> Run Full Test Suite</>
              )}
            </button>
          </div>
        </div>
        
        {/* Current/Selected Test Report */}
        {selectedE2eReport && (
          <E2eReportDetails report={selectedE2eReport} />
        )}
        
        {/* Historical Reports */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Recent Test Runs</h4>
            <button onClick={fetchE2eReports} className="text-sm text-brand hover:underline">
              Refresh
            </button>
          </div>
          {e2eTestReports.length > 0 ? (
            <div className="space-y-2">
              {e2eTestReports.map((report) => (
                <div 
                  key={report.run_id}
                  onClick={() => setSelectedE2eReport(report)}
                  className={`p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedE2eReport?.run_id === report.run_id ? 'border-brand bg-brand/5' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {report.summary?.tests_failed === 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <span className="font-medium text-gray-900">{report.run_id}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          {new Date(report.start_time).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-green-600">{report.summary?.tests_passed} passed</span>
                      {report.summary?.tests_failed > 0 && (
                        <span className="text-red-600">{report.summary?.tests_failed} failed</span>
                      )}
                      <span className="text-gray-500">{report.summary?.pass_rate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No test runs yet. Click "Run Full Test Suite" to start.</p>
          )}
        </div>
      </div>

      {/* Error by Severity */}
      {errorStats?.by_severity && (
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5" /> Errors by Severity
          </h3>
          <div className="flex gap-4 flex-wrap">
            {Object.entries(errorStats.by_severity).map(([severity, count]) => (
              <div key={severity} className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                severity === 'critical' ? 'bg-red-100 text-red-700' :
                severity === 'high' ? 'bg-orange-100 text-orange-700' :
                severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {severity}: {count}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Errors List */}
      <RecentErrorsList 
        systemErrors={systemErrors} 
        fetchAllData={fetchAllData} 
      />

      {/* AI Automated Testing Section */}
      <AiTestingSection 
        aiTestingStatus={aiTestingStatus}
        aiTestHistory={aiTestHistory}
        aiAutoFixes={aiAutoFixes}
        latestTestRun={latestTestRun}
        aiTestingLoading={aiTestingLoading}
        onStartAiTesting={handleStartAiTesting}
        onStopAiTesting={handleStopAiTesting}
        onRunTestsNow={handleRunTestsNow}
      />
    </div>
  );
}

// Sub-component: E2E Report Details
function E2eReportDetails({ report }) {
  return (
    <div className="p-4 bg-gray-50 border-b border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">
          Test Run: {report.run_id}
          <span className="ml-2 text-sm text-gray-500">
            {new Date(report.start_time).toLocaleString()}
          </span>
        </h4>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          report.summary?.tests_failed === 0 
            ? 'bg-green-100 text-green-700' 
            : 'bg-red-100 text-red-700'
        }`}>
          {report.summary?.tests_passed}/{report.summary?.tests_run} Passed
        </span>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-gray-900">{report.summary?.tests_run || 0}</p>
          <p className="text-xs text-gray-500">Tests Run</p>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-green-600">{report.summary?.tests_passed || 0}</p>
          <p className="text-xs text-gray-500">Passed</p>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-red-600">{report.summary?.tests_failed || 0}</p>
          <p className="text-xs text-gray-500">Failed</p>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-amber-600">{report.summary?.issues_found || 0}</p>
          <p className="text-xs text-gray-500">Issues</p>
        </div>
        <div className="bg-white rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-blue-600">{report.summary?.pass_rate || 0}%</p>
          <p className="text-xs text-gray-500">Pass Rate</p>
        </div>
      </div>
      
      {/* Issues List */}
      {report.issues && report.issues.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-3 border-b border-gray-200 bg-red-50">
            <h5 className="font-medium text-red-800">Issues Found ({report.issues.length})</h5>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {report.issues.map((issue, idx) => (
              <div key={issue.id || idx} className="p-3 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium mr-2 ${
                      issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      issue.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                      issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {issue.severity?.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{issue.test_name}</span>
                    <span className="text-xs text-gray-500 ml-2">({issue.category})</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-1">{issue.error}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Cleanup Status */}
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-gray-500">Cleanup:</span>
        {report.cleanup?.success ? (
          <span className="text-green-600 flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> Complete
          </span>
        ) : (
          <span className="text-red-600 flex items-center gap-1">
            <XCircle className="w-4 h-4" /> Errors: {report.cleanup?.errors?.length || 0}
          </span>
        )}
      </div>
    </div>
  );
}

// Sub-component: Recent Errors List
function RecentErrorsList({ systemErrors, fetchAllData }) {
  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Bug className="w-5 h-5 text-red-500" /> Recent Errors ({systemErrors.length})
        </h3>
        {systemErrors.length > 0 && (
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/system/errors/resolve-all`);
                toast.success(res.data.message || "All errors resolved");
                fetchAllData();
              } catch (e) {
                toast.error("Failed to resolve all errors");
              }
            }}
            className="px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg flex items-center gap-1"
            data-testid="resolve-all-errors-btn"
          >
            <Check className="w-4 h-4" /> Resolve All
          </button>
        )}
      </div>
      {systemErrors.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p>No unresolved errors!</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {systemErrors.slice(0, 5).map((err) => (
            <div key={err.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      err.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      err.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                      err.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {err.severity}
                    </span>
                    <span className="text-xs text-gray-500">{err.category}</span>
                    {err.auto_fix_applied && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        Auto-fixed
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-gray-900 text-sm truncate">
                    {err.error_type}: {typeof err.message === 'string' ? err.message?.substring(0, 100) : JSON.stringify(err.message)?.substring(0, 100)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {err.endpoint && `Endpoint: ${err.endpoint} • `}
                    Occurrences: {err.occurrence_count || 1} • 
                    Last seen: {err.last_seen ? new Date(err.last_seen).toLocaleString() : 'Unknown'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/system/errors/${err.id}/resolve`);
                      toast.success("Error marked as resolved");
                      fetchAllData();
                    } catch (e) {
                      toast.error("Failed to resolve error");
                    }
                  }}
                  className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg flex items-center gap-1"
                >
                  <Check className="w-4 h-4" /> Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Sub-component: AI Testing Section
function AiTestingSection({ 
  aiTestingStatus, 
  aiTestHistory, 
  aiAutoFixes, 
  latestTestRun,
  aiTestingLoading,
  onStartAiTesting,
  onStopAiTesting,
  onRunTestsNow
}) {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 overflow-hidden">
      <div className="p-4 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                AI Automated Testing
                {aiTestingStatus?.is_running && (
                  <span className="px-2 py-0.5 bg-green-400 text-green-900 text-xs rounded-full animate-pulse">ACTIVE</span>
                )}
              </h3>
              <p className="text-purple-100 text-sm">Automatically tests all endpoints and UI every hour</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRunTestsNow}
              disabled={aiTestingLoading}
              className="px-4 py-2 bg-white text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-50 disabled:opacity-50 flex items-center gap-2"
              data-testid="run-tests-now-btn"
            >
              {aiTestingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Run Now
            </button>
            {aiTestingStatus?.is_running ? (
              <button
                onClick={onStopAiTesting}
                disabled={aiTestingLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                data-testid="stop-ai-testing-btn"
              >
                <Square className="w-4 h-4" /> Stop
              </button>
            ) : (
              <button
                onClick={onStartAiTesting}
                disabled={aiTestingLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
                data-testid="start-ai-testing-btn"
              >
                <Play className="w-4 h-4" /> Start Hourly
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AI Testing Stats */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <p className="text-xl font-bold text-gray-900">{aiTestingStatus?.total_test_runs || 0}</p>
          <p className="text-xs text-gray-500">Total Runs</p>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <p className="text-xl font-bold text-green-600">{aiTestingStatus?.average_pass_rate || 0}%</p>
          <p className="text-xs text-gray-500">Avg Pass Rate</p>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <p className="text-xl font-bold text-purple-600">{aiTestingStatus?.total_auto_fixes || 0}</p>
          <p className="text-xs text-gray-500">Auto-Fixes</p>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <p className="text-xl font-bold text-blue-600">{aiTestingStatus?.runs_last_24h || 0}</p>
          <p className="text-xs text-gray-500">Last 24h</p>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <p className="text-xl font-bold text-amber-600">{aiTestingStatus?.fix_success_rate || 100}%</p>
          <p className="text-xs text-gray-500">Fix Rate</p>
        </div>
      </div>

      {/* Schedule Info */}
      {aiTestingStatus && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Timer className="w-4 h-4" />
              Interval: {aiTestingStatus.interval_display || '1 hour'}
            </span>
            {aiTestingStatus.last_run && (
              <span>Last: {new Date(aiTestingStatus.last_run).toLocaleString()}</span>
            )}
            {aiTestingStatus.next_run && (
              <span className="text-green-600">Next: {new Date(aiTestingStatus.next_run).toLocaleString()}</span>
            )}
          </div>
        </div>
      )}

      {/* Latest Test Run Results */}
      {latestTestRun && !latestTestRun.message && (
        <div className="px-4 pb-4">
          <div className={`rounded-lg p-4 ${
            latestTestRun.health_status === 'healthy' ? 'bg-green-50 border border-green-200' :
            latestTestRun.health_status === 'degraded' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                {latestTestRun.health_status === 'healthy' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : latestTestRun.health_status === 'degraded' ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                Latest Test: {latestTestRun.health_status?.toUpperCase()}
              </h4>
              <span className="text-sm text-gray-500">
                {new Date(latestTestRun.timestamp).toLocaleString()}
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{latestTestRun.total_tests}</p>
                <p className="text-xs text-gray-500">Total Tests</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{latestTestRun.total_passed}</p>
                <p className="text-xs text-gray-500">Passed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{latestTestRun.total_failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{latestTestRun.total_auto_fixed}</p>
                <p className="text-xs text-gray-500">Auto-Fixed</p>
              </div>
            </div>

            {/* Test breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="bg-white/50 rounded p-2">
                <span className="font-medium">API:</span> {latestTestRun.api_tests?.passed}/{latestTestRun.api_tests?.total}
              </div>
              <div className="bg-white/50 rounded p-2">
                <span className="font-medium">DB:</span> {latestTestRun.database_tests?.passed}/{latestTestRun.database_tests?.total}
              </div>
              <div className="bg-white/50 rounded p-2">
                <span className="font-medium">Integration:</span> {latestTestRun.integration_tests?.passed}/{latestTestRun.integration_tests?.total}
              </div>
              <div className="bg-white/50 rounded p-2">
                <span className="font-medium">UI:</span> {latestTestRun.ui_tests?.passed}/{latestTestRun.ui_tests?.total}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test History */}
      {aiTestHistory.length > 0 && (
        <div className="px-4 pb-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <History className="w-4 h-4" /> Recent Test Runs
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {aiTestHistory.slice(0, 5).map((run, idx) => (
              <div key={run.run_id || idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    run.health_status === 'healthy' ? 'bg-green-500' :
                    run.health_status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-gray-600">{new Date(run.timestamp).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-600">{run.total_passed} passed</span>
                  <span className="text-red-600">{run.total_failed} failed</span>
                  <span className="font-medium">{run.pass_rate}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Fix History */}
      {aiAutoFixes.length > 0 && (
        <div className="px-4 pb-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-purple-600" /> Auto-Fix Log
          </h4>
          <div className="space-y-2 max-h-36 overflow-y-auto">
            {aiAutoFixes.slice(0, 5).map((fix, idx) => (
              <div key={fix.id || idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-sm">
                <div className="flex items-center gap-2">
                  {fix.success ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="font-medium text-gray-900">{fix.issue_type}</span>
                  <span className="text-gray-500">- {fix.fix_applied}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(fix.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
